<template>
  <div class="section">
    <div class="section-title">
      <base-section-title title="缴纳保证金">
        <template v-slot:right-tool>
          <el-button
            v-if="gfPayStatus === depositStatusDict.SUCCESS.value"
            type="primary"
            size="small"
            @click="visible = true"
          >保证金缴纳规则</el-button>
        </template>
      </base-section-title>
    </div>

    <div class="section-content">
      <apply-no-content
        v-if="gfPayStatus === depositStatusDict.NOT_STARTED.value"
      />
      <apply-content-lock
        v-else-if="quoteClosed && unFullPaid"
        title="报价已截止，无法缴纳保证金"
      />
      <template v-else>
        <el-form class="detail-form" label-suffix="：" label-width="120px">
          <el-form-item label="保证金">
            <span>{{
              gfInfo.guaranteeFee ? strToThousands(gfInfo.guaranteeFee) : '-'
            }}
              元</span>
            <money-in-cn :money="gfInfo.guaranteeFee" />
          </el-form-item>
          <el-form-item label="收款户名">
            <span>江西省精彩纵横采购咨询有限公司</span>
          </el-form-item>
          <el-form-item label="汇款账号">
            <div v-if="gfInfo.gfAccount">
              <span>
                {{ gfInfo.gfAccount ? $bankCardFilter(gfInfo.gfAccount) : '-' }}
              </span>
              <span class="text-danger m-l-10">该汇款账号具备唯一性，仅适用于当前用户和当前项目，不可用于其他用户和其他项目转账！</span>
            </div>
            <span v-else>
              虚拟账号获取失败，
              <el-button style="padding: 0" type="text" @click="getAccount">
                重新获取
              </el-button>
            </span>
          </el-form-item>
          <el-form-item label="开户银行">
            <span>{{ gfInfo.gfAccountBank || '-' }}</span>
          </el-form-item>
          <el-form-item label="开户行号">
            <span>
              {{ gfInfo.bankNo ? $bankCardFilter(gfInfo.bankNo) : '-' }}
            </span>
          </el-form-item>
          <el-form-item label="已缴金额">
            <span>{{
              gfInfo.gfPayPaid ? strToThousands(gfInfo.gfPayPaid) : 0
            }}
              元</span>
            <money-in-cn
              style="display: inline-block"
              class="m-l-8"
              :money="gfInfo.gfPayPaid"
            />
            <el-button
              v-if="gfPayStatus !== depositStatusDict.SUCCESS.value"
              type="text"
              icon="el-icon-refresh"
              class="refresh-button"
              :disabled="!gfInfo.gfAccount"
              style="padding: 0"
              @click="refresh"
            >
              刷新到账信息
            </el-button>
          </el-form-item>
          <el-form-item label="缴纳状态">
            <span
              v-if="gfPayStatus"
              :class="`text-${gfPayStatusDictValue.type || 'danger'}`"
            >
              {{ gfPayStatusDictValue.label || '待缴纳' }}
            </span>
          </el-form-item>
          <el-form-item label="缴纳时间">
            <span>{{ gfInfo.gfPayTime || ' - ' }}</span>
          </el-form-item>
        </el-form>

        <template v-if="gfPayStatus !== depositStatusDict.SUCCESS.value">
          <el-divider />
          <deposit-rules />
        </template>
      </template>
    </div>

    <!-- 保证金弹窗规则 -->
    <el-dialog
      :visible="visible"
      title="保证金规则"
      width="50rem"
      class="dialog"
      :close-on-click-modal="false"
      @close="visible = false"
    >
      <div class="card no-padding no-margin">
        <div class="section">
          <div class="section-content flex-center">
            <deposit-rules />
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
// 公共组件
import BaseSectionTitle from '@/components/common/BaseSectionTitle'
import ApplyNoContent from '@/components/common/ApplyNoContent'
import DepositRules from '@/components/copywriter/DepositCopywriter'
import ApplyContentLock from '@/components/common/ApplyContentLock'
import MoneyInCn from '@/components/common/MoneyInCn'
// 数据字典
import { depositStatusDict } from '@/dictionary/project/common'
// 工具方法
import { strToThousands } from '@/utils/utils'

export default {
  name: 'PayGuaranteeFee',

  components: {
    BaseSectionTitle,
    ApplyNoContent,
    DepositRules,
    ApplyContentLock,
    MoneyInCn
  },

  props: {
    // 报价已截止，无法缴纳保证金
    quoteClosed: {
      type: Boolean,
      default: false
    },

    // 保证金缴纳状态
    gfPayStatus: {
      type: [String, Number],
      default: depositStatusDict.NOT_STARTED.value
    },

    // 保证金交易信息
    tradeGf: {
      type: Object,
      default: () => {}
    }
  },

  data() {
    return {
      visible: false, // 保证金规则弹窗是否可见
      depositStatusDict, // 保证金缴纳状态
      // 显示保证金账号提示信息
      showPopover: false,
      /**
       * 保证金缴纳信息
       * @type {Object}
       * @property {string} guaranteeFee 保证金金额
       * @property {string} gfAccount 汇款账号
       * @property {string} gfAccountBank 开户银行
       * @property {string} bankNo 开户行号
       * @property {string} gfPayPaid 已缴金额
       * @property {string} gfPayTime 缴纳时间
       */
      gfInfo: {}
    }
  },

  computed: {
    // 保证金缴纳状态字典翻译值
    gfPayStatusDictValue() {
      return depositStatusDict.translate(this.gfPayStatus)
    },

    // 未缴足保证金
    unFullPaid() {
      return this.gfPayStatus !== depositStatusDict.SUCCESS.value
    }
  },

  watch: {
    tradeGf: {
      handler: function(val) {
        if (val.guaranteeFee) this.setGFInfo(val)
      },
      immediate: true,
      deep: true
    }
  },

  created() {
    this.showPopover = true
  },

  methods: {
    strToThousands,

    // 查询保证金账户信息
    getAccount() {
      this.$emit('getAccount')
    },

    // 获取保证金缴纳信息
    setGFInfo(data) {
      this.gfInfo = {
        guaranteeFee: data.guaranteeFee || '0.00',
        gfAccount: data.gfAccount || '',
        gfAccountBank: data.gfAccountBank || '',
        bankNo: data.bankNo || '',
        gfPayPaid: data.gfPayPaid || '0.00',
        gfPayTime: data.gfPayTime || ''
      }
    },

    // 刷新到账信息
    refresh() {
      this.$emit('refresh')
    }
  }
}
</script>

<style lang="scss" scoped>
.refresh-button {
  margin-left: pixel-to-rem($n: 20);
}

.tip-button--info {
  font-size: 14px;
  color: $info-color;
  cursor: pointer;
  margin-left: 6px;
}
</style>
